import { BarChart3, FileCheck, Home, LogOut, MapPin, Menu, Shield, User, X } from 'lucide-react'
import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../utils/AuthContext'

function Navbar() {
  const { user, isAuthenticated, logout } = useAuth()
  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = React.useState(false)

  const handleLogout = async () => {
    await logout()
    navigate('/')
    setIsMenuOpen(false)
  }

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const closeMenu = () => {
    setIsMenuOpen(false)
  }

  return (
    <nav className="bg-black/90 backdrop-blur-md shadow-lg border-b border-green-500/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link 
              to="/" 
              className="flex items-center space-x-2"
              onClick={closeMenu}
            >
              <Shield className="h-8 w-8 text-green-500" />
              <span className="text-xl font-mono font-bold text-green-400">
                <span className="text-green-500">&lt;</span>
                Cyber<span className="text-blue-400">Safe</span>
                <span className="text-green-500">/&gt;</span>
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {isAuthenticated ? (
              <>
                <Link 
                  to="/dashboard" 
                  className="flex items-center space-x-1 text-gray-300 hover:text-green-400 transition-all duration-300 relative group font-mono"
                >
                  <Home className="h-4 w-4" />
                  <span>Dashboard</span>
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-green-400 group-hover:w-full transition-all duration-300"></span>
                </Link>
                
                <Link 
                  to="/verify" 
                  className="flex items-center space-x-1 text-gray-300 hover:text-green-400 transition-all duration-300 relative group font-mono"
                >
                  <FileCheck className="h-4 w-4" />
                  <span>Verify</span>
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-green-400 group-hover:w-full transition-all duration-300"></span>
                </Link>

                {user?.role === 'admin' && (
                  <>
                    <Link 
                      to="/admin" 
                      className="flex items-center space-x-1 text-gray-300 hover:text-green-400 transition-all duration-300 relative group font-mono"
                    >
                      <BarChart3 className="h-4 w-4" />
                      <span>Admin</span>
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-green-400 group-hover:w-full transition-all duration-300"></span>
                    </Link>
                    <Link 
                      to="/map" 
                      className="flex items-center space-x-1 text-gray-300 hover:text-green-400 transition-all duration-300 relative group font-mono"
                    >
                      <MapPin className="h-4 w-4" />
                      <span>Geo Map</span>
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-green-400 group-hover:w-full transition-all duration-300"></span>
                    </Link>
                  </>
                )}

                <div className="relative group">
                  <button className="flex items-center space-x-1 text-gray-300 hover:text-green-400 transition-colors font-mono">
                    <User className="h-4 w-4" />
                    <span>{user?.full_name || user?.username}</span>
                  </button>
                  
                  {/* Dropdown Menu */}
                  <div className="absolute right-0 mt-2 w-48 bg-black/95 backdrop-blur-md border border-green-500/30 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="py-1">
                      <Link 
                        to="/profile" 
                        className="block px-4 py-2 text-sm text-gray-300 hover:text-green-400 hover:bg-green-500/10 font-mono"
                      >
                        Profile Settings
                      </Link>
                      <button 
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:text-red-400 hover:bg-red-500/10 font-mono"
                      >
                        <div className="flex items-center space-x-2">
                          <LogOut className="h-4 w-4" />
                          <span>[Logout]</span>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className="text-gray-300 hover:text-blue-400 transition-all duration-300 relative group font-mono"
                >
                  Login
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-400 group-hover:w-full transition-all duration-300"></span>
                </Link>
                <Link 
                  to="/register" 
                  className="bg-green-600/20 hover:bg-green-600/40 text-green-400 hover:text-green-300 px-4 py-2 rounded-md font-mono transition-all duration-300 border border-green-600/30 hover:border-green-500/50"
                >
                  <span className="text-green-500">{`{`}</span>Register<span className="text-green-500">{`}`}</span>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button 
              onClick={toggleMenu}
              className="text-gray-300 hover:text-green-400 focus:outline-none focus:text-green-400 transition-colors"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-black/95 backdrop-blur-md border-t border-green-500/30">
              {isAuthenticated ? (
                <>
                  <Link 
                    to="/dashboard" 
                    className="flex items-center space-x-2 px-3 py-2 text-gray-300 hover:text-green-400 hover:bg-green-500/10 rounded-md transition-colors font-mono"
                    onClick={closeMenu}
                  >
                    <Home className="h-4 w-4" />
                    <span>Dashboard</span>
                  </Link>
                  
                  <Link 
                    to="/verify" 
                    className="flex items-center space-x-2 px-3 py-2 text-gray-300 hover:text-green-400 hover:bg-green-500/10 rounded-md transition-colors font-mono"
                    onClick={closeMenu}
                  >
                    <FileCheck className="h-4 w-4" />
                    <span>Verify</span>
                  </Link>

                  {user?.role === 'admin' && (
                    <>
                      <Link 
                        to="/admin" 
                        className="flex items-center space-x-2 px-3 py-2 text-gray-300 hover:text-green-400 hover:bg-green-500/10 rounded-md transition-colors font-mono"
                        onClick={closeMenu}
                      >
                        <BarChart3 className="h-4 w-4" />
                        <span>Admin</span>
                      </Link>
                      <Link 
                        to="/map" 
                        className="flex items-center space-x-2 px-3 py-2 text-gray-300 hover:text-green-400 hover:bg-green-500/10 rounded-md transition-colors font-mono"
                        onClick={closeMenu}
                      >
                        <MapPin className="h-4 w-4" />
                        <span>Geo Map</span>
                      </Link>
                    </>
                  )}

                  <Link 
                    to="/profile" 
                    className="flex items-center space-x-2 px-3 py-2 text-gray-300 hover:text-green-400 hover:bg-green-500/10 rounded-md transition-colors font-mono"
                    onClick={closeMenu}
                  >
                    <User className="h-4 w-4" />
                    <span>Profile</span>
                  </Link>

                  <button 
                    onClick={handleLogout}
                    className="flex items-center space-x-2 w-full text-left px-3 py-2 text-gray-300 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors font-mono"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>[Logout]</span>
                  </button>

                  <div className="px-3 py-2 border-t border-green-500/30 mt-2">
                    <p className="text-sm text-gray-400 font-mono">
                      Logged in: <span className="text-green-400">{user?.username}</span>
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="block px-3 py-2 text-gray-300 hover:text-blue-400 hover:bg-blue-500/10 rounded-md transition-colors font-mono"
                    onClick={closeMenu}
                  >
                    Login
                  </Link>
                  <Link 
                    to="/register" 
                    className="block px-3 py-2 text-green-400 bg-green-600/20 hover:bg-green-600/40 rounded-md transition-colors font-mono border border-green-600/30"
                    onClick={closeMenu}
                  >
                    Register
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}

export default Navbar